import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  StudyModel,
  type StudyPermissionModel,
  LoadingStateModel,
  type StudyImportDiff,
  type INewStudy,
  type StudySchemaExport,
  DEFAULT_CASE_TEAM_LABEL,
  DEFAULT_CASE_TEAM_SUBSCRIBERS_LABEL
} from '@/models';
import StudyService from '@/services/studyService';
import { LocalStorageUtils, routeConstants } from '@/utils';
import { useRoute, useRouter } from 'vue-router';
import { useDashboardStore } from './dashboard';
import { useEntryListStore } from './entryList';
import { useFormStore } from './forms';
import { usePipelineStore } from './pipeline';
import { createRequestToken } from './requestToken';
import { useErrorStore } from './error';
import { translate } from '@/utils/translate';


export const useStudyStore = defineStore('study', () => {
  const router = useRouter();
  const route = useRoute();
  const dashboardStore = useDashboardStore();
  const entryListStore = useEntryListStore();
  const formStore = useFormStore();
  const pipelineStore = usePipelineStore();

  const studies = ref<StudyModel[]>([]);
  const studyLoader = ref(new LoadingStateModel({isLoading: false}));
  const selectedStudy = ref<StudyModel>();
  const selectedStudyId = ref<string>();
  /**
   * The study `setStudy` is switching to, from its first line until the switch lands. The study
   * picker replaces the route without waiting for the switch, so the shell's route watcher reads
   * this to tell that route from a URL change of its own. A failed switch stays named, so the route
   * it was started with does not start it again; the next switch replaces it.
   */
  const selectingStudyId = ref<string>();
  /**
   * Selections overtake each other: only the latest `setStudy`, or `getStudies` unless it started
   * beside a switch to the routed study, writes the selection.
   */
  const switchRequest = createRequestToken();
  /** Whether a `setStudy` holds the selection and has not landed it yet, forms included. */
  let switchUnderWay = false;
  /**
   * Whether `getStudies` began selecting with nothing selected before, and no selection has landed
   * since. A switch that fails in that state has no study behind it to leave on screen.
   */
  let nothingSelected = false;
  const studyPermissions = ref<StudyPermissionModel>();
  /**
   * The study `studyPermissions` was read for, as the selection named it rather than as the record's
   * `uri` spells it, since that name is the route's. Across a switch the route names the new study
   * before its grant lands, so a screen gated on the routed study checks this rather than trusting
   * the grant it holds (ENG-10284). `landPermissions` writes the two together, so they never disagree.
   */
  const permissionsStudyId = ref<string>();
  const landPermissions = (permissions: StudyPermissionModel, studyId: string) => {
    studyPermissions.value = permissions;
    permissionsStudyId.value = studyId;
  };
  /**
   * The reader's grant in `studyId`, once it has landed for that study. Across a switch the grant in
   * hand is the study just left's, which says nothing about this one.
   */
  const grantFor = (studyId: string | undefined): StudyPermissionModel['effective'] | undefined =>
    studyId !== undefined && permissionsStudyId.value === studyId ? studyPermissions.value?.effective : undefined;
  /**
   * Whether the reader's account is staff, as the last permissions read said. It is the account's,
   * not the study's, so it holds across a switch. A read that did not say reads as no.
   */
  const isStaff = computed(() => studyPermissions.value?.staff === true);

  /**
   * Whether this study runs the Perfusion Companion app. Off unless the flag says otherwise, so a
   * study that has not been set up for it shows none of the scan features.
   */
  const isPerfusionCompanionStudy = computed(
    () => selectedStudy.value?.flags?.perfusionCompanion === true
  );

  /**
   * The study as the Perfusion Companion endpoints name it: `:id` on each of them. Empty with no
   * study selected, which the scans panel reads as nothing to load.
   */
  const pumpCenterId = computed(
    () => selectedStudyId.value ?? selectedStudy.value?.identifier ?? ''
  );

  /**
   * Whether this study runs the per-case team roster. Off unless the flag says
   * otherwise, so a study that has not been set up for it keeps the one-click
   * case creation it has always had and shows none of the roster UI.
   */
  const isCaseTeamEnabled = computed(
    () => selectedStudy.value?.flags?.caseTeam?.enable === true
  );

  /** The study's role list and labels, or undefined when the flag is off. */
  const caseTeamConfig = computed(() =>
    isCaseTeamEnabled.value ? selectedStudy.value?.flags?.caseTeam : undefined
  );

  /** Section heading, falling back to the neutral platform wording. */
  const caseTeamLabel = computed(
    () => caseTeamConfig.value?.label?.trim() || DEFAULT_CASE_TEAM_LABEL
  );

  /**
   * What the study calls a case-team role, by its key; the key itself for one the study's config no
   * longer lists, since case-team rows keep the key they were stored with. Read whether or not the
   * roster is switched on, because the rows outlive the flag.
   */
  const caseTeamRoleLabel = (roleKey: string) =>
    selectedStudy.value?.flags?.caseTeam?.roles?.find(role => role.key === roleKey)?.label || roleKey;

  const caseTeamSubscribersLabel = computed(
    () =>
      caseTeamConfig.value?.subscribersLabel?.trim() ||
      DEFAULT_CASE_TEAM_SUBSCRIBERS_LABEL
  );

  /**
   * Whether this study has case notification rules switched on.
   *
   * Note this says the rules are *configured*, not that anything sends them —
   * no evaluator reads the flag yet. It exists so the roster UI can tell the two
   * situations apart: a case team with notifications behind it means something
   * different to the person muting a member than one without.
   */
  const isCaseNotificationsEnabled = computed(
    () => selectedStudy.value?.flags?.caseNotifications?.enable === true
  );

  /** The study's rules and anchors, or undefined when the flag is off. */
  const caseNotificationsConfig = computed(() =>
    isCaseNotificationsEnabled.value ?
      selectedStudy.value?.flags?.caseNotifications :
      undefined
  );


  /**
   * The app's first load. This had no try/catch at all, and neither did its callers, so a failure here
   * was an unhandled rejection that left `StudyFetchContainer` spinning forever with nothing on screen
   * — the symptom this ticket describes. A failure is now fatal-tier: the error screen replaces the
   * outlet, with retry wired back to this function.
   */
  const getStudies = async () => {
    // It selects a study as `setStudy` does, so a switch started while it reads overtakes it, and a
    // switch that failed before it is over. Beside a switch to the routed study it reads the list
    // alone: the settings saves refresh it unawaited, and the switch still has the forms to read.
    const leavesToSwitch =
      switchUnderWay && route.params.studyIdentifier === selectingStudyId.value;
    const token = leavesToSwitch ? undefined : switchRequest.claim();
    const selects = () => token !== undefined && switchRequest.holds(token);
    if (selects()) {
      selectingStudyId.value = undefined;
      switchUnderWay = false;
      nothingSelected = !studyPermissions.value;
    }
    studyLoader.value.startLoading();
    // Before the list is asked for: the dashboard's views read the selected study from their first
    // render. The forms read used to select the routed study on its way through, and on a refreshed
    // URL it was the first to; nothing but this store selects one now.
    if (selects() && route.params.studyIdentifier) {
      selectedStudyId.value = route.params.studyIdentifier as string;
    }
    let listLanded = false;
    try {
      studies.value = await StudyService.getStudies();
      listLanded = true;

      studies.value.sort((a, b) => new Date(b.creation).getTime() - new Date(a.creation).getTime());
      if (!selects()) return;

      if (studies.value.length === 0) {
        selectedStudy.value = undefined;
        selectedStudyId.value = undefined;
        dashboardStore.setUserOptionLinks();
        return;
      }

      if (route.params.studyIdentifier) {
        selectedStudyId.value = route.params.studyIdentifier as string;
        const record = await StudyService.getStudy(route.params.studyIdentifier as string);
        if (!selects()) return;
        selectedStudy.value = record;
      } else {
        const firstStudyId = studies.value[0].identifier;
        const record = await StudyService.getStudy(firstStudyId);
        if (!selects()) return;
        selectedStudy.value = record;
        selectedStudyId.value = firstStudyId;

        //set selected study in the cache
        LocalStorageUtils.setStudyDetails(selectedStudy.value);
      }

      const permissions = await StudyService.getStudyPermissions(selectedStudy.value!.identifier);
      if (!selects()) return;
      landPermissions(permissions, selectedStudyId.value!);
      dashboardStore.setDashboardLinks();
    } catch (error) {
      // Not rethrown: the caller's `if (!selectedStudyId)` branch falls through harmlessly while the
      // error screen renders over it. A study or grant read a switch overtook is that switch's to
      // report, and a list refreshed beside a switch fails with the dashboard still up behind it.
      if (selects()) {
        studyLoader.value.setFatalError(error, () => getStudies());
      } else if (!listLanded) {
        // A toast, past the loader: the loader is the switch's, and the switch has not failed.
        useErrorStore().handle(error);
      }
    } finally {
      // Always runs now — the old code only reached `stopLoadingAndFinish` on the happy path. A read
      // that did not select leaves the loader to the switch.
      if (selects()) {
        nothingSelected = false;
        studyLoader.value.stopLoadingAndFinish();
      }
    }
  };

  const getStudy = async (identifier: string) => {
    try {
      selectedStudy.value = await StudyService.getStudy(identifier);
      selectedStudyId.value = identifier;

      //set selected study in the cache
      LocalStorageUtils.setStudyDetails(selectedStudy.value);
    } catch (error) {
      studyLoader.value.setFatalError(error, () => getStudy(identifier));
    }
  };

  const setStudyFromCache = () => {
    const studyId = LocalStorageUtils.getStudyIdentifier();
    if (studyId) {
      selectedStudyId.value = studyId;
    }
  };

  const setStudy = async (study: StudyModel) => {
    const token = switchRequest.claim();
    switchUnderWay = true;
    selectingStudyId.value = study.identifier;
    //unselect selected case
    studyLoader.value.startLoading();
    try {
      entryListStore.selectedCase = undefined;
      const record = await StudyService.getStudy(study.identifier);
      if (!switchRequest.holds(token)) return;
      selectedStudy.value = record;
      LocalStorageUtils.setStudyDetails(record);
      selectedStudyId.value = study.identifier;

      let permissions: StudyPermissionModel;
      try {
        permissions = await StudyService.getStudyPermissions(record.identifier);
      } catch (error) {
        // With no grant to say the forms read would be refused, it is asked all the same, since until
        // it starts the forms list still holds the study just left. It settles its own failure.
        if (switchRequest.holds(token)) formStore.getStudyForms(study.identifier);
        throw error;
      }
      if (!switchRequest.holds(token)) return;
      landPermissions(permissions, study.identifier);
      dashboardStore.setDashboardLinks();
      nothingSelected = false;

      // Behind the grant rather than beside it, so a reader it says `GET study/:id/forms` would refuse
      // is not asked, and not toasted for their own access (ENG-10317). The study loader is up until
      // then, so the section draws nothing from the list the switch has not replaced yet. Under way
      // until the forms land too, so a list refresh in the meantime leaves them to it.
      await formStore.getStudyForms(study.identifier);
      if (!switchRequest.holds(token)) return;
      selectingStudyId.value = undefined;
      switchUnderWay = false;
    } catch (error) {
      if (switchRequest.holds(token)) {
        // A toast over the study the reader still has, unless there is none: then it reports as the
        // first load it took over would have.
        if (nothingSelected) {
          studyLoader.value.setFatalError(error, () => getStudies());
        } else {
          studyLoader.value.setError(error);
        }
      }
    } finally {
      // An overtaken switch leaves the loader to the one that overtook it.
      if (switchRequest.holds(token)) {
        switchUnderWay = false;
        studyLoader.value.stopLoadingAndFinish();
      }
    }
  };

  /** The import changes the last check returned, held only while the import dialog shows them. */
  const studyImportDiff = ref<StudyImportDiff>();
  const importExportLoader = ref(new LoadingStateModel({ isLoading: false }));
  /** Counts checks and clears, so a check that answers after a newer one, or after a clear, is dropped. */
  let importCheck = 0;
  /** Drops the import changes, and any check still on its way. */
  const clearStudyImportDiff = () => {
    importCheck += 1;
    studyImportDiff.value = undefined;
  };
  const importAndValidateStudy = async (file: File) => {
    importExportLoader.value.startLoading();
    // Cleared first: a check that fails would otherwise leave the previous file's import changes
    // behind, and Approve would apply them.
    clearStudyImportDiff();
    const check = importCheck;
    try {
      const importDiff = await StudyService.importAndValidate(
        selectedStudy.value!.identifier,
        file,
        selectedStudy.value?.flags?.customDataDictionary
      );
      if (check === importCheck) {
        studyImportDiff.value = importDiff;
      }
    } catch (error) {
      importExportLoader.value.setError(error);
    } finally {
      importExportLoader.value.stopLoadingAndFinish();
    }
  };

  const getExport = async (payload: StudySchemaExport) => {
    importExportLoader.value.startLoading();
    try {
      await StudyService.getStudyExport(selectedStudy.value!.identifier, payload);
    } catch (error) {
      importExportLoader.value.setError(error);
    } finally {
      importExportLoader.value.stopLoadingAndFinish();
    }
  };

  /**
   * Applies the import changes the check returned, and answers whether it did. A refusal leaves them
   * on screen to try again, except a 409: the study's forms moved since the check, so these import
   * changes are stale and go, and the toast carries the server's word to import the file again.
   */
  const importAndApproveStudy = async (): Promise<boolean> => {
    const importDiff = studyImportDiff.value;
    if (!importDiff) {
      return false;
    }
    importExportLoader.value.startLoading();
    try {
      await StudyService.importStudy(selectedStudy.value!.identifier, {
        ...importDiff,
        expectedVersion: importDiff.version
      });
      importExportLoader.value.setSuccess(translate('study_import_success'));
      clearStudyImportDiff();
      formStore.getStudyForms(selectedStudy.value!.identifier);
      return true;
    } catch (error) {
      const failure = importExportLoader.value.setError(error);
      if (failure.httpStatus === 409) {
        clearStudyImportDiff();
      }
      return false;
    } finally {
      importExportLoader.value.stopLoadingAndFinish();
    }
  };

  const createStudy = async (
    newStudy: Omit<INewStudy, 'organizationIdentifier'>,
    redirect: boolean = true
  ): Promise<string | undefined> => {
    studyLoader.value.startLoading();
    try {
      const { data } = await StudyService.postStudy({
        ...newStudy,
        organizationIdentifier: selectedStudy.value?.organizationIdentifier
      });
      const createdStudyIdentifier = data.hexIdentifier || data.identifier;
      await getStudies();
      await setStudy({ ...data, identifier: createdStudyIdentifier });
      pipelineStore.resetStore();
      if (redirect && createdStudyIdentifier) {
        await router.push({
          name: routeConstants.FORMS.name,
          params: { studyIdentifier: createdStudyIdentifier }
        });
      }
      return createdStudyIdentifier;
    } catch (error) {
      studyLoader.value.setError(error);
    } finally {
      studyLoader.value.stopLoadingAndFinish();
    }
  };

  const importStudyForm = async (fileName: string, filepath: string) => {
    studyLoader.value.startLoading();
    try {
      await StudyService.importStudyForm(selectedStudy.value!.identifier, fileName, filepath);
    } catch (error) {
      studyLoader.value.setError(error);
    } finally {
      studyLoader.value.stopLoadingAndFinish();
    }
  };

  return {
    studies,
    selectedStudy,
    selectedStudyId,
    selectingStudyId,
    studyPermissions,
    permissionsStudyId,
    grantFor,
    isStaff,
    isPerfusionCompanionStudy,
    pumpCenterId,
    isCaseTeamEnabled,
    caseTeamConfig,
    caseTeamLabel,
    caseTeamRoleLabel,
    caseTeamSubscribersLabel,
    isCaseNotificationsEnabled,
    caseNotificationsConfig,
    studyLoader,
    importExportLoader,
    studyImportDiff,
    clearStudyImportDiff,
    getStudies,
    getStudy,
    setStudyFromCache,
    setStudy,
    createStudy,
    importAndValidateStudy,
    importAndApproveStudy,
    getExport,
    importStudyForm
  };
});
